import type { ChatMessage, UploadItem } from "../types/redux";
import { migrateMessageContent } from "./chatMigrations";

const COMPLETE_PROGRESS = 100;

type UploadedFile = { fileName?: string; fileSize?: number; docId?: string };

export interface RehydratedHistory {
  messages: ChatMessage[];
  todoPlans: Record<string, { plan: unknown; scopeSummary: unknown }>;
}

/** Rebuild the upload map for a turn persisted via upload_batch_complete. */
function buildUploads(files: UploadedFile[]): Record<string, UploadItem> {
  const uploads: Record<string, UploadItem> = {};

  files.forEach((file, index) => {
    const id = file.docId || `file-${index}`;
    uploads[id] = {
      id,
      fileName: file.fileName,
      fileSize: file.fileSize,
      status: "Complete",
      completed: true,
      error: false,
      progress: COMPLETE_PROGRESS,
      docId: file.docId,
    };
  });

  return uploads;
}

/** A file-upload turn renders as an upload table plus its summary text. */
function pushFileUploadTurn(target: ChatMessage[], turn: any, files: UploadedFile[]): void {
  target.push({
    sender: "chatbot",
    type: "upload-table",
    turnId: turn.turnId,
    uploads: buildUploads(files),
    allCompleted: true,
    text: "",
    timestamp: turn.createdAt,
  } as ChatMessage);

  if (turn.finalChatResponse) {
    target.push({
      sender: "chatbot",
      text: migrateMessageContent(turn.finalChatResponse),
      timestamp: turn.createdAt,
      turnId: turn.turnId,
      type: "upload-summary",
    });
  }

  console.log("📦 [REHYDRATE] Restored file upload turn:", {
    turnId: turn.turnId,
    fileCount: files.length,
    files: files.map((file) => file.fileName),
  });
}

/** A regular turn renders as the user's question followed by the bot's answer. */
function pushConversationTurn(target: ChatMessage[], turn: any): void {
  if (turn.userInputText) {
    target.push({
      sender: "user",
      text: turn.userInputText,
      timestamp: turn.createdAt,
      turnId: turn.turnId,
    });
  }

  if (!turn.finalChatResponse) return;

  target.push({
    sender: "chatbot",
    // Phase 8: migrate the old fact format (^fid=xxx^) to [[FACT:xxx]] so
    // messages stored before the refactor still render clickable citations
    text: migrateMessageContent(turn.finalChatResponse),
    timestamp: turn.createdAt,
    turnId: turn.turnId,
    inlineOrder: [], // not persisted — facts are parsed back out of the text
    // Restore suggested_actions (UI buttons like Preview Draft, Download DOCX)
    suggested_actions: turn.suggestedActions || undefined,
  });
}

/** Turn the backend's turn list into the messages and todo plans the UI holds. */
export function buildRehydratedHistory(turns: any[]): RehydratedHistory {
  const messages: ChatMessage[] = [];
  const todoPlans: RehydratedHistory["todoPlans"] = {};

  for (const turn of turns) {
    const fileUploadBatch = turn.mergedContext?.file_upload_batch;
    const isFileUploadTurn = turn.userInputText === "[FILE_UPLOAD_BATCH]" || fileUploadBatch;

    if (isFileUploadTurn && fileUploadBatch?.files) {
      pushFileUploadTurn(messages, turn, fileUploadBatch.files as UploadedFile[]);
      continue; // Skip regular message handling for file upload turns
    }

    pushConversationTurn(messages, turn);

    if (turn.todoPlan && turn.turnId) {
      todoPlans[turn.turnId] = {
        plan: turn.todoPlan,
        scopeSummary: turn.scopePlan, // Backend uses scopePlan, not scopeSummary
      };
    }
  }

  return { messages, todoPlans };
}
